import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  FormGroup,
  FormControlLabel,
  Switch,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Chip,
  Tooltip,
  Stack,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import {
  MoreVert,
  Close,
  CenterFocusStrong,
  Map as MapIcon
} from '@mui/icons-material';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useAPI } from '../../hooks/useAPI';
import useWebSocket from '../../hooks/useWebSocket';
import { positionInterpolator, SnappingMode } from '../../utils/positionInterpolator';

// Set your Mapbox access token
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

// Create start/finish marker element for routes (matching dashboard style)
const createRouteMarkerElement = (type: 'start' | 'finish'): HTMLElement => {
  const el = document.createElement('div');
  const circleColor = type === 'start' ? '#22C55E' : '#000000'; // Green for start, black for finish

  el.style.cssText = `
    background-color: ${circleColor};
    border: 3px solid white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 12px;
    font-weight: bold;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    cursor: pointer;
    user-select: none;
  `;
  el.textContent = type === 'start' ? 'S' : 'F';
  return el;
};

// Utility function to format time ago
const formatTimeAgo = (timestamp: string | Date): string => {
  const now = new Date();
  const lastUpdate = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - lastUpdate.getTime()) / 1000);

  if (diffInSeconds < 5) return 'just now';
  if (diffInSeconds < 60) return `${diffInSeconds}s ago`;

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ago`;
};

// Utility function to determine tracker status based on data freshness
const getTrackerStatus = (lastUpdate: string | Date, originalStatus?: string): 'active' | 'standby' | 'offline' => {
  const now = new Date();
  const lastUpdateTime = new Date(lastUpdate);
  const diffInMinutes = Math.floor((now.getTime() - lastUpdateTime.getTime()) / (1000 * 60));

  // If more than 30 minutes without data, consider offline
  if (diffInMinutes >= 30) {
    return 'offline';
  }

  // If more than 5 minutes without data, consider standby
  if (diffInMinutes >= 5) {
    return 'standby';
  }

  // Recent data, keep as active
  return 'active';
};

interface SpeedMetrics {
  current: number;
  average: number;
  max: number;
  smoothed: number;
}

interface ElevationMetrics {
  current: number;
  gain: number;
  loss: number;
  grade: number;
}

interface TrackerData {
  trackerId: string;
  title?: string;
  currentRole: string;
  status: 'active' | 'standby' | 'offline';
  position: {
    lat: number;
    lng: number;
  };
  speed?: SpeedMetrics | number;
  elevation?: ElevationMetrics | number;
  batteryLevel?: number;
  signalStrength?: number;
  lastUpdate?: string;
  routeDistance?: number; // Distance along route from start (km)
}

interface DashboardData {
  trackers: TrackerData[];
  activeRoute?: {
    raceId: string;
    raceName: string;
    stageName: string;
    totalDistance: number;
    hasCoordinates: boolean;
  };
}

const LiveCGMap: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const routeMarkers = useRef<{ start?: mapboxgl.Marker; finish?: mapboxgl.Marker }>({});
  const lastLoggedRouteId = useRef<string | null>(null);

  // State
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 = Visibility, 1 = Route Snapping
  const [trackerVisibility, setTrackerVisibility] = useState<Record<string, boolean>>(() => {
    // Load from localStorage on mount
    const saved = localStorage.getItem('liveCGTrackerVisibility');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error('Failed to parse saved tracker visibility:', error);
      }
    }
    return {};
  });
  const [trackerSnappingModes, setTrackerSnappingModes] = useState<Record<string, 'none' | 'magnetic' | 'always'>>(() => {
    // Load from localStorage on mount
    const saved = localStorage.getItem('liveCGTrackerSnappingModes');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error('Failed to parse saved tracker snapping modes:', error);
      }
    }
    return {};
  });
  const [followTracker, setFollowTracker] = useState<string>('none');
  const [userInteracting, setUserInteracting] = useState(false);

  // Position interpolation for smooth movement
  const [interpolatedPositions, setInterpolatedPositions] = useState<Map<string, { lat: number; lng: number }>>(new Map());

  // Camera path playback state (recording moved to remote control)
  const [savedPaths, setSavedPaths] = useState<any[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRemoteRecording, setIsRemoteRecording] = useState(false);
  const cameraPositionIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // API and WebSocket hooks
  const api = useAPI();
  const { socket, connected } = useWebSocket(process.env.REACT_APP_WS_URL || 'http://localhost:8000');

  // Initialize position interpolator
  useEffect(() => {
    positionInterpolator.setUpdateCallback((positions) => {
      setInterpolatedPositions(new Map(positions));
    });

    return () => {
      positionInterpolator.clear();
    };
  }, []);

  // Memoize trackers with computed status
  const trackers = useMemo(() => {
    if (!dashboardData?.trackers) return [];

    return dashboardData.trackers
      .filter(tracker => tracker.position && tracker.position.lat && tracker.position.lng)
      .map(tracker => {
        // Compute actual status based on data freshness
        const computedStatus = tracker.lastUpdate
          ? getTrackerStatus(tracker.lastUpdate, tracker.status)
          : 'offline';

        // Apply route snapping before feeding to interpolator
        const speed = typeof tracker.speed === 'object' ? tracker.speed?.current : tracker.speed || 0;
        const snappingMode = trackerSnappingModes[tracker.trackerId] || 'none';
        const gpsPosition = { lat: tracker.position.lat, lng: tracker.position.lng };

        // Snap position to route if mode is not 'none'
        // Use tracker's route distance if available (calculated by backend)
        const snappedPosition = positionInterpolator.snapToRoute(
          gpsPosition,
          snappingMode,
          tracker.routeDistance || 0
        );

        // Feed snapped position to interpolator for smooth animation
        // Pass snappingMode and routeDistance for route-following animation and extrapolation
        positionInterpolator.updateTrackerPosition(
          tracker.trackerId,
          snappedPosition,
          speed,
          snappingMode,
          tracker.routeDistance
        );

        return {
          ...tracker,
          status: computedStatus
        };
      })
      .filter(tracker => tracker.status !== 'offline'); // Hide offline trackers
  }, [dashboardData?.trackers, trackerSnappingModes]);

  const activeRoute = dashboardData?.activeRoute;

  // Memoize the route data to prevent unnecessary re-renders
  const stableRouteId = useMemo(() => {
    const raceId = activeRoute?.raceId || null;
    // Only log when raceId changes
    if (raceId) {
      console.log('LiveCGMap: stableRouteId computed:', { raceId });
    }
    return raceId;
  }, [activeRoute?.raceId]);

  const stableRouteData = useMemo(() => {
    const data = {
      raceName: activeRoute?.raceName,
      stageName: activeRoute?.stageName,
      totalDistance: activeRoute?.totalDistance,
      hasCoordinates: activeRoute?.hasCoordinates
    };
    // Only log when hasCoordinates changes to avoid spam
    if (data.hasCoordinates) {
      console.log('LiveCGMap: stableRouteData computed:', { data });
    }
    return data;
  }, [activeRoute?.raceName, activeRoute?.stageName, activeRoute?.totalDistance, activeRoute?.hasCoordinates]);

  // Fetch initial dashboard data (WebSocket provides real-time updates)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('/dashboard/live-cg');
        // Only log when route ID changes to avoid spam
        const currentRouteId = response.data.data.activeRoute?.raceId;
        if (currentRouteId && currentRouteId !== lastLoggedRouteId.current) {
          console.log('LiveCGMap: Dashboard API response - Route found:', {
            raceId: currentRouteId,
            hasCoordinates: response.data.data.activeRoute.hasCoordinates
          });
          lastLoggedRouteId.current = currentRouteId;
        }
        setDashboardData(response.data.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    // Fetch once on mount - WebSocket handles real-time updates
    fetchData();

    // Optional: Fallback polling only if WebSocket disconnects (60s interval)
    const interval = setInterval(() => {
      if (!connected) {
        console.log('LiveCGMap: WebSocket disconnected, using fallback polling');
        fetchData();
      }
    }, 60000); // Fallback every 60 seconds only when disconnected

    return () => clearInterval(interval);
  }, [api, connected]);

  // WebSocket real-time updates
  useEffect(() => {
    if (!socket || !connected) return;

    const handleTrackerUpdate = (data: any) => {
      console.log('LiveCGMap: Real-time tracker update received:', data);
      setDashboardData(prevData => {
        if (!prevData) return prevData;

        const updatedTrackers = [...prevData.trackers];
        const trackerIndex = updatedTrackers.findIndex(t => t.trackerId === data.trackerId);

        if (trackerIndex !== -1) {
          updatedTrackers[trackerIndex] = {
            ...updatedTrackers[trackerIndex],
            ...data,
            lastUpdate: new Date().toISOString()
          };
        }

        return {
          ...prevData,
          trackers: updatedTrackers
        };
      });
    };

    const handleDashboardUpdate = (data: any) => {
      console.log('LiveCGMap: Full dashboard update received:', data);
      setDashboardData(data);
    };

    // Remote control handlers
    const handleCameraMove = (data: { direction: 'up' | 'down' | 'left' | 'right'; speed: number }) => {
      console.log('LiveCGMap: Received camera-move event', data);
      if (!map.current) return;

      const moveDistance = 0.005 * data.speed; // Increased from 0.001 - faster pan/move
      const center = map.current.getCenter();

      let newLat = center.lat;
      let newLng = center.lng;

      switch (data.direction) {
        case 'up':
          newLat += moveDistance;
          break;
        case 'down':
          newLat -= moveDistance;
          break;
        case 'left':
          newLng -= moveDistance;
          break;
        case 'right':
          newLng += moveDistance;
          break;
      }

      map.current.easeTo({
        center: [newLng, newLat],
        duration: 100
      });
    };

    const handleCameraZoom = (data: { direction: 'in' | 'out'; speed: number }) => {
      if (!map.current) return;

      const currentZoom = map.current.getZoom();
      const zoomDelta = 0.05 * data.speed; // Decreased from 0.1 - slower zoom

      map.current.easeTo({
        zoom: data.direction === 'in' ? currentZoom + zoomDelta : currentZoom - zoomDelta,
        duration: 100
      });
    };

    const handleCameraRotate = (data: { direction: 'left' | 'right'; speed: number }) => {
      if (!map.current) return;

      const currentBearing = map.current.getBearing();
      const rotateDelta = 1 * data.speed; // Decreased from 2 - slower rotate

      map.current.easeTo({
        bearing: data.direction === 'right' ? currentBearing + rotateDelta : currentBearing - rotateDelta,
        duration: 100
      });
    };

    const handleCameraTilt = (data: { direction: 'up' | 'down'; speed: number }) => {
      if (!map.current) return;

      const currentPitch = map.current.getPitch();
      const tiltDelta = 0.5 * data.speed; // Decreased from 1 - slower tilt

      const newPitch = data.direction === 'up'
        ? Math.min(currentPitch + tiltDelta, 85)
        : Math.max(currentPitch - tiltDelta, 0);

      map.current.easeTo({
        pitch: newPitch,
        duration: 100
      });
    };

    const handleCameraReset = () => {
      if (!map.current) return;

      map.current.easeTo({
        center: [101.9758, 4.2105],
        zoom: 6,
        pitch: 45,
        bearing: 0,
        duration: 1000
      });
    };

    // Remote recording handlers
    const handleRecordingStarted = () => {
      console.log('LiveCGMap: Recording started from remote');
      setIsRemoteRecording(true);

      // Start sending camera position updates every 100ms
      cameraPositionIntervalRef.current = setInterval(() => {
        if (map.current && socket && connected) {
          const center = map.current.getCenter();
          const zoom = map.current.getZoom();
          const bearing = map.current.getBearing();
          const pitch = map.current.getPitch();

          socket.emit('camera:position-update', {
            center: { lat: center.lat, lng: center.lng },
            zoom,
            bearing,
            pitch
          });
        }
      }, 100);
    };

    const handleRecordingStopped = () => {
      console.log('LiveCGMap: Recording stopped from remote');
      setIsRemoteRecording(false);

      // Stop sending camera position updates
      if (cameraPositionIntervalRef.current) {
        clearInterval(cameraPositionIntervalRef.current);
        cameraPositionIntervalRef.current = null;
      }

      refreshPaths();
    };

    const handlePlayPath = async (data: { pathId: string }) => {
      console.log('LiveCGMap: Play path from remote', data.pathId);
      await playPath(data.pathId);
    };

    const handlePathDeleted = () => {
      console.log('LiveCGMap: Path deleted from remote');
      refreshPaths();
    };

    // Subscribe to WebSocket events (backend uses colon format: dashboard:update)
    socket.on('tracker-update', handleTrackerUpdate);
    socket.on('dashboard:update', handleDashboardUpdate); // Fixed: backend emits dashboard:update
    socket.on('gps-update', handleTrackerUpdate);
    socket.on('remote:camera-move', handleCameraMove);
    socket.on('remote:camera-zoom', handleCameraZoom);
    socket.on('remote:camera-rotate', handleCameraRotate);
    socket.on('remote:camera-tilt', handleCameraTilt);
    socket.on('remote:camera-reset', handleCameraReset);
    socket.on('remote:recording-started', handleRecordingStarted);
    socket.on('remote:recording-stopped', handleRecordingStopped);
    socket.on('remote:play-path', handlePlayPath);
    socket.on('remote:path-deleted', handlePathDeleted);

    return () => {
      socket.off('tracker-update', handleTrackerUpdate);
      socket.off('dashboard:update', handleDashboardUpdate);
      socket.off('gps-update', handleTrackerUpdate);
      socket.off('remote:camera-move', handleCameraMove);
      socket.off('remote:camera-zoom', handleCameraZoom);
      socket.off('remote:camera-rotate', handleCameraRotate);
      socket.off('remote:camera-tilt', handleCameraTilt);
      socket.off('remote:camera-reset', handleCameraReset);
      socket.off('remote:recording-started', handleRecordingStarted);
      socket.off('remote:recording-stopped', handleRecordingStopped);
      socket.off('remote:play-path', handlePlayPath);
      socket.off('remote:path-deleted', handlePathDeleted);
    };
  }, [socket, connected]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    const initializeMap = async () => {
      try {
        map.current = new mapboxgl.Map({
          container: mapContainer.current!,
          style: 'mapbox://styles/wedobroadcast/cmftsafww00j801sd8ifhcmir',
          center: [101.9758, 4.2105], // Center of Malaysia
          zoom: 6,
          pitch: 45,
          bearing: 0,
          attributionControl: false,
        });

        // Add compact attribution control
        map.current.addControl(new mapboxgl.AttributionControl({
          compact: true
        }), 'bottom-left');

        // Enable pitch and zoom controls
        map.current.addControl(new mapboxgl.NavigationControl({
          showCompass: true,
          showZoom: true,
          visualizePitch: true
        }), 'top-left');

        map.current.on('load', () => {
          if (!map.current) return;

          // Add tracker source
          map.current.addSource('trackers', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: []
            }
          });

          // Add tracker symbols
          map.current.addLayer({
            id: 'tracker-symbols',
            type: 'symbol',
            source: 'trackers',
            layout: {
              'icon-image': ['get', 'icon'],
              'icon-size': 1.0, // Increased to 1.0 to maintain visibility with smaller canvas
              'icon-allow-overlap': true,
              'text-field': ['get', 'label'],
              'text-font': ['Noto Sans Bold', 'Arial Unicode MS Bold'],
              'text-size': 11,
              'text-offset': [0, 2],
              'text-anchor': 'top',
              'text-allow-overlap': false
            },
            paint: {
              'text-color': '#000000',
              'text-halo-width': 0
            }
          });

          // Add beacon images (smaller orange circles)
          const beacons = ['active', 'standby', 'offline'];
          beacons.forEach(status => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = 24; // Smaller size (was 32)
            canvas.height = 24; // Smaller size (was 32)

            if (ctx) {
              const centerX = canvas.width / 2;
              const centerY = canvas.height / 2;
              const radius = 8; // Smaller radius (was 12)

              // Outer circle (border)
              ctx.beginPath();
              ctx.arc(centerX, centerY, radius + 2, 0, 2 * Math.PI);
              ctx.fillStyle = '#000000';
              ctx.fill();

              // Inner circle (orange color for all trackers)
              ctx.beginPath();
              ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
              ctx.fillStyle = '#f59e0b'; // Orange color for all trackers
              ctx.fill();

              // Add pulsing effect for active trackers
              if (status === 'active') {
                ctx.globalAlpha = 0.6;
                ctx.beginPath();
                ctx.arc(centerX, centerY, radius + 3, 0, 2 * Math.PI); // Smaller pulse (was +4)
                ctx.fillStyle = '#f59e0b'; // Orange pulse
                ctx.fill();
              }
            }

            if (ctx) {
              map.current!.addImage(`beacon-${status}`, ctx.getImageData(0, 0, canvas.width, canvas.height));
            }
          });

          // Handle tracker clicks for popup
          map.current.on('click', 'tracker-symbols', (e) => {
            if (!map.current || !e.features || e.features.length === 0) return;

            const feature = e.features[0];
            const coordinates = (feature.geometry as any).coordinates.slice();
            const properties = feature.properties!;

            // Create popup content
            const timeAgo = properties.lastUpdate ? formatTimeAgo(properties.lastUpdate) : 'Unknown';
            const popupContent = `
              <div style="width: 280px; padding: 12px; font-family: Roboto, sans-serif; background-color: #1a1a1a; color: #fff; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.5);">
                <h3 style="margin: 0 0 8px 0; font-size: 1.1rem; font-weight: 600; color: #fff;">
                  ${properties.label}
                </h3>
                <p style="margin: 0 0 12px 0; font-size: 0.75rem; color: #b0b0b0;">
                  ID: ${properties.trackerId}
                </p>
                <div style="margin-bottom: 8px;">
                  <span style="font-size: 0.85rem; color: #fff;">
                    📍 ${coordinates[1].toFixed(4)}, ${coordinates[0].toFixed(4)}
                  </span>
                </div>
                <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                  <span style="font-size: 0.85rem; color: #fff;">⚡ ${properties.speed?.toFixed(1) || 0} km/h</span>
                  <span style="font-size: 0.85rem; color: #fff;">⛰️ ${properties.elevation?.toFixed(0) || 0}m</span>
                </div>
                <div style="display: flex; justify-content: space-between; margin-bottom: 12px;">
                  <span style="font-size: 0.85rem; color: #fff;">🔋 ${properties.battery || 0}%</span>
                  <span style="font-size: 0.85rem; color: #fff;">📶 ${properties.signal || 0}/5</span>
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <span style="background-color: ${properties.status === 'active' ? '#22c55e' : properties.status === 'standby' ? '#f59e0b' : '#ef4444'}; color: white; padding: 4px 8px; border-radius: 12px; font-size: 0.7rem; text-transform: uppercase; font-weight: 600;">
                    ${properties.status}
                  </span>
                  <span style="font-size: 0.7rem; color: #b0b0b0;">
                    ${timeAgo}
                  </span>
                </div>
              </div>
            `;

            new mapboxgl.Popup({
              className: 'dark-popup'
            })
              .setLngLat(coordinates)
              .setHTML(popupContent)
              .addTo(map.current!);
          });

          // Change cursor on hover
          map.current.on('mouseenter', 'tracker-symbols', () => {
            if (map.current) map.current.getCanvas().style.cursor = 'pointer';
          });

          map.current.on('mouseleave', 'tracker-symbols', () => {
            if (map.current) map.current.getCanvas().style.cursor = '';
          });

          // Track user interaction to pause following
          map.current.on('dragstart', () => setUserInteracting(true));
          map.current.on('zoomstart', () => setUserInteracting(true));
          map.current.on('rotatestart', () => setUserInteracting(true));
          map.current.on('pitchstart', () => setUserInteracting(true));

          // Resume following after interaction ends
          map.current.on('dragend', () => {
            setTimeout(() => setUserInteracting(false), 2000); // Resume after 2 seconds
          });
          map.current.on('zoomend', () => {
            setTimeout(() => setUserInteracting(false), 2000);
          });
          map.current.on('rotateend', () => {
            setTimeout(() => setUserInteracting(false), 2000);
          });
          map.current.on('pitchend', () => {
            setTimeout(() => setUserInteracting(false), 2000);
          });
        });

      } catch (error) {
        console.error('Error initializing map:', error);
      }
    };

    initializeMap();

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Update trackers when data changes
  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded()) return;

    // Filter visible trackers
    const visibleTrackers = trackers.filter(tracker =>
      trackerVisibility[tracker.trackerId] !== false
    );

    // Create GeoJSON features for visible trackers using interpolated positions
    const features = visibleTrackers.map(tracker => {
      // Use interpolated position if available, otherwise fall back to actual position
      const interpolatedPos = interpolatedPositions.get(tracker.trackerId);
      const position = interpolatedPos || tracker.position;

      return {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [position.lng, position.lat]
        },
        properties: {
          trackerId: tracker.trackerId,
          icon: `beacon-${tracker.status}`,
          label: (tracker.title || tracker.currentRole).replace(/_/g, ' ').toUpperCase(),
          status: tracker.status,
          speed: typeof tracker.speed === 'object' ? tracker.speed?.current : tracker.speed || 0,
          elevation: typeof tracker.elevation === 'object' ? tracker.elevation?.current : tracker.elevation || 0,
          battery: tracker.batteryLevel || 0,
          signal: tracker.signalStrength || 0,
          lastUpdate: tracker.lastUpdate
        }
      };
    });

    // Update the tracker source
    const source = map.current.getSource('trackers');
    if (source && source.type === 'geojson') {
      source.setData({
        type: 'FeatureCollection',
        features
      });
    }

    // Follow selected tracker (only center, preserve zoom/tilt) using interpolated position
    if (followTracker !== 'none' && !userInteracting) {
      const trackerToFollow = visibleTrackers.find(t => t.trackerId === followTracker);
      if (trackerToFollow && map.current) {
        const interpolatedPos = interpolatedPositions.get(trackerToFollow.trackerId);
        const position = interpolatedPos || trackerToFollow.position;

        map.current.easeTo({
          center: [position.lng, position.lat],
          duration: 300 // Shorter duration for smoother following
          // Don't change zoom, pitch, or bearing - preserve user settings
        });
      }
    }

  }, [trackers, trackerVisibility, followTracker, userInteracting, interpolatedPositions]);

  // Load route if available
  useEffect(() => {
    if (!map.current) return;

    console.log('LiveCGMap: Route useEffect triggered', {
      stableRouteId,
      hasMap: !!map.current,
      hasCoordinates: stableRouteData.hasCoordinates,
      isStyleLoaded: map.current.isStyleLoaded()
    });

    // Wait for map to be ready if not already loaded
    if (!map.current.isStyleLoaded()) {
      const onStyleLoad = () => {
        console.log('LiveCGMap: Style loaded, now loading route');
        loadRoute();
      };
      map.current.once('styledata', onStyleLoad);
      return;
    }

    loadRoute();

    function loadRoute() {
      if (!map.current) return;

      const routeSourceId = 'active-route';
      const routeLayerId = 'active-route-layer';

      // Remove existing route if any
      if (map.current.getLayer(routeLayerId)) {
        map.current.removeLayer(routeLayerId);
      }
      if (map.current.getSource(routeSourceId)) {
        map.current.removeSource(routeSourceId);
      }

      // Remove existing route markers
      if (routeMarkers.current.start) {
        routeMarkers.current.start.remove();
        routeMarkers.current.start = undefined;
      }
      if (routeMarkers.current.finish) {
        routeMarkers.current.finish.remove();
        routeMarkers.current.finish = undefined;
      }

      // Load route coordinates if available - check both stableRouteId and activeRoute directly
      if ((stableRouteId && stableRouteData.hasCoordinates) || (activeRoute?.raceId && activeRoute?.hasCoordinates)) {
        const routeId = stableRouteId || activeRoute?.raceId;
        console.log('LiveCGMap: Loading route coordinates for', routeId);

        const loadRouteCoordinates = async () => {
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/active-route-coordinates`);
            if (!response.ok) {
              console.warn('LiveCGMap: Failed to load route coordinates:', response.statusText);
              return;
            }

            const result = await response.json();
            if (result.success && result.data.coordinates) {
              const coordinates = result.data.coordinates;
              console.log('LiveCGMap: Rendering route with', coordinates.length, 'coordinates');

              // Cache route coordinates in position interpolator for route snapping
              positionInterpolator.setRouteCoordinates(
                coordinates.map((coord: any) => ({ lat: coord.lat, lng: coord.lng }))
              );

              const routeGeoJSON = {
                type: 'Feature' as const,
                properties: {
                  name: stableRouteData.raceName || activeRoute?.raceName || 'Active Route',
                  stage: stableRouteData.stageName || activeRoute?.stageName || '',
                  distance: stableRouteData.totalDistance || activeRoute?.totalDistance || 0
                },
                geometry: {
                  type: 'LineString' as const,
                  coordinates: coordinates.map((coord: any) => [coord.lng, coord.lat])
                }
              };

              if (map.current) {
                map.current.addSource(routeSourceId, {
                  type: 'geojson',
                  data: routeGeoJSON
                });

                map.current.addLayer({
                  id: routeLayerId,
                  type: 'line',
                  source: routeSourceId,
                  layout: {
                    'line-join': 'round',
                    'line-cap': 'round'
                  },
                  paint: {
                    'line-color': '#EF4444',
                    'line-width': 3,
                    'line-opacity': 1.0
                  }
                });

                console.log('LiveCGMap: Route displayed:', stableRouteData.raceName || activeRoute?.raceName, 'with', coordinates.length, 'points');

                // Add start and finish markers
                if (coordinates.length > 0) {
                  const startCoord = coordinates[0];
                  const finishCoord = coordinates[coordinates.length - 1];

                  // Create start marker using dashboard style
                  const startElement = createRouteMarkerElement('start');
                  routeMarkers.current.start = new mapboxgl.Marker(startElement)
                    .setLngLat([startCoord.lng, startCoord.lat])
                    .addTo(map.current!);

                  // Create finish marker using dashboard style
                  const finishElement = createRouteMarkerElement('finish');
                  routeMarkers.current.finish = new mapboxgl.Marker(finishElement)
                    .setLngLat([finishCoord.lng, finishCoord.lat])
                    .addTo(map.current!);

                  console.log('LiveCGMap: Start and finish markers added with S/F letters');
                }
              }
            }
          } catch (error) {
            console.error('Error loading route coordinates:', error);
          }
        };

        loadRouteCoordinates();
      } else {
        console.log('LiveCGMap: Not loading coordinates', {
          hasActiveRoute: !!stableRouteData,
          hasCoordinates: stableRouteData.hasCoordinates,
          stableRouteId
        });
      }
    }
  }, [stableRouteId, stableRouteData]);

  // Initialize tracker visibility when trackers change
  useEffect(() => {
    const newVisibility = { ...trackerVisibility };
    let changed = false;
    trackers.forEach(tracker => {
      if (!(tracker.trackerId in newVisibility)) {
        newVisibility[tracker.trackerId] = true; // Show all trackers by default
        changed = true;
      }
    });
    if (changed) {
      setTrackerVisibility(newVisibility);
      // Save to localStorage
      localStorage.setItem('liveCGTrackerVisibility', JSON.stringify(newVisibility));
    }
  }, [trackers, trackerVisibility]);

  // Initialize tracker snapping modes when trackers change
  useEffect(() => {
    const newSnappingModes = { ...trackerSnappingModes };
    let changed = false;
    trackers.forEach(tracker => {
      if (!(tracker.trackerId in newSnappingModes)) {
        newSnappingModes[tracker.trackerId] = 'none'; // Default: no snapping (raw GPS)
        changed = true;
      }
    });
    if (changed) {
      setTrackerSnappingModes(newSnappingModes);
      // Save to localStorage
      localStorage.setItem('liveCGTrackerSnappingModes', JSON.stringify(newSnappingModes));
    }
  }, [trackers, trackerSnappingModes]);

  const handleTrackerVisibilityToggle = (trackerId: string) => {
    setTrackerVisibility(prev => {
      const updated = {
        ...prev,
        [trackerId]: !prev[trackerId]
      };
      // Save to localStorage
      localStorage.setItem('liveCGTrackerVisibility', JSON.stringify(updated));
      return updated;
    });
  };

  const visibleTrackerCount = trackers.filter(tracker => trackerVisibility[tracker.trackerId] !== false).length;


  // Fetch saved camera paths
  useEffect(() => {
    const fetchPaths = async () => {
      try {
        const response = await api.get('/map-cg/camera-path/list');
        if (response.data.success) {
          setSavedPaths(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching camera paths:', error);
      }
    };

    fetchPaths();
  }, [api]);

  // Camera path playback function
  const refreshPaths = async () => {
    try {
      const response = await api.get('/map-cg/camera-path/list');
      if (response.data.success) {
        setSavedPaths(response.data.data);
      }
    } catch (error) {
      console.error('Error refreshing camera paths:', error);
    }
  };

  const playPath = async (pathId: string) => {
    try {
      const response = await api.get(`/map-cg/camera-path/${pathId}`);
      if (response.data.success && map.current) {
        const path = response.data.data;
        setIsPlaying(true);

        // Play back the camera movements
        let currentIndex = 0;
        const playbackInterval = setInterval(() => {
          if (currentIndex >= path.points.length) {
            clearInterval(playbackInterval);
            setIsPlaying(false);
            return;
          }

          const point = path.points[currentIndex];
          if (map.current) {
            map.current.easeTo({
              center: [point.center.lng, point.center.lat],
              zoom: point.zoom,
              bearing: point.bearing,
              pitch: point.pitch,
              duration: 100
            });
          }

          currentIndex++;
        }, 100);
      }
    } catch (error) {
      console.error('Error playing path:', error);
      setIsPlaying(false);
    }
  };

  return (
    <Box sx={{ height: '100vh', width: '100vw', position: 'relative', bgcolor: '#000' }}>
      {/* Map Container */}
      <div
        ref={mapContainer}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%'
        }}
      />

      {/* Tracker Visibility Control Button */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1000
        }}
      >
        <Tooltip title="Tracker Visibility Controls">
          <IconButton
            onClick={() => setSideMenuOpen(true)}
            sx={{
              backgroundColor: 'rgba(26, 26, 26, 0.9)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(26, 26, 26, 1)',
                color: 'white'
              },
              boxShadow: 2
            }}
          >
            <MoreVert />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Status Info */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          zIndex: 1000
        }}
      >
        <Card sx={{ backgroundColor: 'rgba(26, 26, 26, 0.9)', color: 'white' }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <MapIcon fontSize="small" />
                <Typography variant="caption">
                  {visibleTrackerCount} / {trackers.length} trackers
                </Typography>
              </Stack>
              {followTracker !== 'none' && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CenterFocusStrong fontSize="small" color="primary" />
                  <Typography variant="caption" color="primary.main">
                    Following: {trackers.find(t => t.trackerId === followTracker)?.title ||
                              trackers.find(t => t.trackerId === followTracker)?.currentRole}
                  </Typography>
                </Stack>
              )}
              {userInteracting && (
                <Chip
                  label="Manual Control"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
              {isRemoteRecording && (
                <Chip
                  label="Recording"
                  size="small"
                  color="error"
                  sx={{
                    animation: 'blink 1s infinite',
                    '@keyframes blink': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 }
                    }
                  }}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Side Menu Drawer */}
      <Drawer
        anchor="right"
        open={sideMenuOpen}
        onClose={() => setSideMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            backgroundColor: '#1a1a1a',
            color: 'white'
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Tracker Controls</Typography>
            <IconButton
              onClick={() => setSideMenuOpen(false)}
              sx={{ color: 'white' }}
            >
              <Close />
            </IconButton>
          </Stack>

          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)', mb: 2 }} />

          {/* Camera Following */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Camera Following
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Follow Tracker</InputLabel>
              <Select
                value={followTracker}
                onChange={(e) => setFollowTracker(e.target.value)}
                label="Follow Tracker"
                sx={{
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.8)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2'
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              >
                <MenuItem value="none">None (Manual)</MenuItem>
                {trackers.map((tracker) => (
                  <MenuItem key={tracker.trackerId} value={tracker.trackerId}>
                    {tracker.title || tracker.currentRole}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Camera will center on selected tracker while preserving your zoom and perspective
            </Typography>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)', mb: 2 }} />

          {/* Camera Path Playback Status */}
          {savedPaths.length > 0 && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Camera Path Playback
                </Typography>
                <Stack spacing={1}>
                  {savedPaths.map((path) => (
                    <Card
                      key={path.pathId}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2">{path.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {path.pointCount} points • {(path.duration / 1000).toFixed(1)}s
                            </Typography>
                          </Box>
                          {isPlaying && (
                            <Chip
                              label="Playing"
                              size="small"
                              color="success"
                              sx={{ minWidth: 60 }}
                            />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Use Remote Control page to play or delete
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)', mb: 2 }} />
            </>
          )}

          {/* Tabs for Visibility and Route Snapping */}
          <Box>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                mb: 2,
                '& .MuiTab-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  minHeight: 48,
                  '&.Mui-selected': {
                    color: '#1976d2'
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#1976d2'
                }
              }}
            >
              <Tab label={`Visibility (${visibleTrackerCount}/${trackers.length})`} />
              <Tab label="Route Snapping" />
            </Tabs>

            {/* Tab Panel 0: Tracker Visibility */}
            {activeTab === 0 && (
              <Box>
                <FormGroup>
              {trackers.map((tracker) => (
                <FormControlLabel
                  key={tracker.trackerId}
                  control={
                    <Switch
                      checked={trackerVisibility[tracker.trackerId] !== false}
                      onChange={() => handleTrackerVisibilityToggle(tracker.trackerId)}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1976d2'
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#1976d2'
                        }
                      }}
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">
                          {tracker.title || tracker.currentRole}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tracker.trackerId}
                        </Typography>
                      </Box>
                      <Chip
                        label={tracker.status}
                        size="small"
                        color={
                          tracker.status === 'active' ? 'success' :
                          tracker.status === 'standby' ? 'warning' : 'error'
                        }
                        sx={{ minWidth: 60 }}
                      />
                      {tracker.lastUpdate && (
                        <Typography variant="caption" color="text.secondary">
                          {formatTimeAgo(tracker.lastUpdate)}
                        </Typography>
                      )}
                    </Stack>
                  }
                  sx={{
                    width: '100%',
                    mb: 1,
                    '& .MuiFormControlLabel-label': {
                      width: '100%'
                    }
                  }}
                />
              ))}
            </FormGroup>

            {trackers.length === 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                No trackers available
              </Typography>
            )}
              </Box>
            )}

            {/* Tab Panel 1: Route Snapping */}
            {activeTab === 1 && (
              <Box>

            {/* Global Apply All */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  value="none"
                  displayEmpty
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                    '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' }
                  }}
                  id="global-snapping-mode"
                >
                  <MenuItem value="none">None (Raw GPS)</MenuItem>
                  <MenuItem value="magnetic">Magnetic (50m)</MenuItem>
                  <MenuItem value="always">Always Snap</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const select = document.getElementById('global-snapping-mode') as HTMLSelectElement;
                  const mode = (select?.value || 'none') as SnappingMode;
                  const updated: Record<string, SnappingMode> = {};
                  trackers.forEach(tracker => {
                    updated[tracker.trackerId] = mode;
                  });
                  setTrackerSnappingModes(updated);
                  localStorage.setItem('liveCGTrackerSnappingModes', JSON.stringify(updated));
                }}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  },
                  minWidth: '100px'
                }}
              >
                Apply All
              </Button>
            </Stack>

            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)', mb: 2 }} />

            {/* Per-Tracker Snapping */}
            <Stack spacing={1.5}>
              {trackers.map((tracker) => (
                <Box key={tracker.trackerId}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {tracker.title || tracker.currentRole}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tracker.trackerId}
                      </Typography>
                    </Box>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <Select
                        value={trackerSnappingModes[tracker.trackerId] || 'none'}
                        onChange={(e) => {
                          const mode = e.target.value as SnappingMode;
                          setTrackerSnappingModes(prev => {
                            const updated = { ...prev, [tracker.trackerId]: mode };
                            localStorage.setItem('liveCGTrackerSnappingModes', JSON.stringify(updated));
                            return updated;
                          });
                        }}
                        sx={{
                          color: 'white',
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                          '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' }
                        }}
                      >
                        <MenuItem value="none">None</MenuItem>
                        <MenuItem value="magnetic">Magnetic</MenuItem>
                        <MenuItem value="always">Always</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Box>
              ))}
            </Stack>

            {trackers.length === 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                No trackers available
              </Typography>
            )}
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default LiveCGMap;